import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Classroom, Assignment, Submission, ClassroomProgress } from '../types/classroom';
import { useAuth } from './SupabaseAuthContext';
import {
  getTeacherClassrooms,
  getTeacherAssignments,
  getAssignmentProgress,
  createClassroom,
  createAssignment
} from '../data/mockClassrooms';

interface ClassroomContextType {
  // State
  classrooms: Classroom[];
  assignments: Assignment[];
  submissions: Submission[];
  loading: boolean;
  error: string | null;
  
  // Actions
  loadClassrooms: () => Promise<void>;
  loadAssignments: () => Promise<void>;
  createNewClassroom: (classroomData: Omit<Classroom, 'id' | 'createdAt' | 'classCode' | 'teacherId'>) => Promise<Classroom>;
  createNewAssignment: (assignmentData: Omit<Assignment, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Assignment>;
  getProgressData: (assignmentId: string) => Promise<ClassroomProgress>;
  
  // Utilities
  getClassroomById: (id: string) => Classroom | undefined;
  getAssignmentById: (id: string) => Assignment | undefined;
  getSubmissionsForAssignment: (assignmentId: string) => Submission[];
  getStudentSubmission: (assignmentId: string, studentId: string) => Submission | undefined;
}

const ClassroomContext = createContext<ClassroomContextType | undefined>(undefined);

function useClassroom() {
  const context = useContext(ClassroomContext);
  if (context === undefined) {
    throw new Error('useClassroom must be used within a ClassroomProvider');
  }
  return context;
}

interface ClassroomProviderProps {
  children: ReactNode;
}

const ClassroomProvider: React.FC<ClassroomProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [submissions] = useState<Submission[]>([]); // TODO: Implement submission loading
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadClassrooms = async () => {
    if (!user || user.type !== 'teacher') return;
    
    setLoading(true);
    setError(null);
    
    try {
      const teacherClassrooms = await getTeacherClassrooms(user.id);
      setClassrooms(teacherClassrooms);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load classrooms');
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    if (!user || user.type !== 'teacher') return;
    
    setLoading(true);
    setError(null);
    
    try {
      const teacherAssignments = await getTeacherAssignments(user.id);
      setAssignments(teacherAssignments);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignments');
    } finally {
      setLoading(false);
    }
  };

  const createNewClassroom = async (classroomData: Omit<Classroom, 'id' | 'createdAt' | 'classCode' | 'teacherId'>) => {
    if (!user || user.type !== 'teacher') {
      throw new Error('Only teachers can create classrooms');
    }
    
    const newClassroom = await createClassroom({
      ...classroomData,
      teacherId: user.id
    });
    
    setClassrooms(prev => [...prev, newClassroom]);
    return newClassroom;
  };

  const createNewAssignment = async (assignmentData: Omit<Assignment, 'id' | 'createdAt' | 'updatedAt'>) => {
    const newAssignment = await createAssignment(assignmentData);
    setAssignments(prev => [...prev, newAssignment]);
    return newAssignment;
  };

  const getProgressData = async (assignmentId: string): Promise<ClassroomProgress> => {
    return await getAssignmentProgress(assignmentId);
  };

  // Utility functions
  const getClassroomById = (id: string) => classrooms.find(c => c.id === id);
  const getAssignmentById = (id: string) => assignments.find(a => a.id === id);
  const getSubmissionsForAssignment = (assignmentId: string) => 
    submissions.filter(s => s.assignmentId === assignmentId);
  const getStudentSubmission = (assignmentId: string, studentId: string) => 
    submissions.find(s => s.assignmentId === assignmentId && s.studentId === studentId);

  // Load data when user changes
  useEffect(() => {
    if (user && user.type === 'teacher') {
      loadClassrooms();
      loadAssignments();
    }
  }, [user]);

  const value: ClassroomContextType = {
    classrooms,
    assignments,
    submissions,
    loading,
    error,
    loadClassrooms,
    loadAssignments,
    createNewClassroom,
    createNewAssignment,
    getProgressData,
    getClassroomById,
    getAssignmentById,
    getSubmissionsForAssignment,
    getStudentSubmission
  };

  return (
    <ClassroomContext.Provider value={value}>
      {children}
    </ClassroomContext.Provider>
  );
};

export default ClassroomProvider;
export { useClassroom };